import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { getListingsById } from '../../ducks/marketplaceData.duck';
import { FormattedMessage } from '../../util/reactIntl';
import { recentListings } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { useSharedResize } from '../../hooks/useSharedResize';

import HomePageListingCard from '../HomePageListingCard/HomePageListingCard';
import IconSpinner from '../IconSpinner/IconSpinner';
import NamedLink from '../NamedLink/NamedLink';
import { transformListingForCard } from '../../util/listingHelpers';

import css from './RecentListings.module.css';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;

const RecentListings = props => {
  const { className, rootClassName, onListingsFetched } = props;
  const dispatch = useDispatch();
  const [listings, setListings] = useState([]);
  const [includedEntities, setIncludedEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const classes = classNames(rootClassName || css.root, className);
  const carouselContainerId = 'recent-listings-container';
  const sliderId = 'recent-listings-slider';
  const hasListings = listings.length > 0;

  useEffect(() => {
    const fetchRecentListings = async () => {
      try {
        setLoading(true);

        const response = await recentListings();
        const fetchedListings = response.data || [];
        const includedEntities = response.included || [];

        // Add entities to Redux store for proper normalization
        if (includedEntities.length > 0) {
          dispatch(
            addMarketplaceEntities({
              data: {
                data: fetchedListings,
                included: includedEntities,
              },
            })
          );
        }

        // Store all included entities for potential use
        setIncludedEntities(includedEntities);

        // Denormalize listings data - add images and author directly to listings
        const denormalizedListings = fetchedListings.map(listing => {
          const denormalizedListing = { ...listing };

          // Add author data directly to listing
          if (listing.relationships?.author?.data?.id) {
            const authorId = listing.relationships.author.data.id.uuid;
            const author = includedEntities.find(
              entity => entity.type === 'user' && entity.id.uuid === authorId
            );
            if (author) {
              denormalizedListing.author = author;
            }
          }

          // Add images directly to listing
          if (listing.relationships?.images?.data) {
            const imageIds = listing.relationships.images.data.map(img => img.id.uuid);
            const images = includedEntities.filter(
              entity => entity.type === 'image' && imageIds.includes(entity.id.uuid)
            );
            // Sort images to maintain order
            images.sort((a, b) => {
              const aIndex = imageIds.indexOf(a.id.uuid);
              const bIndex = imageIds.indexOf(b.id.uuid);
              return aIndex - bIndex;
            });
            denormalizedListing.images = images;
          }

          return denormalizedListing;
        });

        setListings(denormalizedListings);

        if (onListingsFetched && fetchedListings.length > 0) {
          onListingsFetched(response);
        }

        setError(null);
      } catch (err) {
        console.error('RecentListings fetch error:', err);
        setError(err);
        setListings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentListings();
  }, [onListingsFetched]);

  // Determine number of columns based on viewport size
  const getNumColumns = (width) => {
    if (width >= 1024) return 4; // desktop: 4 cards
    if (width >= 768) return 2; // tablet: 2 cards
    return 1; // mobile: 1 card
  };

  const [numColumns, setNumColumns] = useState(() =>
    typeof window !== 'undefined' ? getNumColumns(window.innerWidth) : 1
  );

  // Single shared resize handler (replaces 2 separate event listeners)
  useSharedResize(({ width, isMobile, isDebounced }) => {
    // Update carousel width
    if (hasListings) {
      const elem = window.document.getElementById(carouselContainerId);
      if (elem) {
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        const elementWidth =
          elem.clientWidth >= width - scrollbarWidth ? width : elem.clientWidth;
        const carouselWidth = elementWidth - scrollbarWidth;
        elem.style.setProperty('--carouselWidth', `${carouselWidth}px`);
      }
    }

    // Update number of columns (only on debounced/final resize)
    if (isDebounced) {
      const newNumColumns = getNumColumns(width);
      if (newNumColumns !== numColumns) {
        setNumColumns(newNumColumns);
        setCurrentSlide(0);
      }
    }
  }, { debounceOnly: false }); // Get both immediate and debounced updates

  // Track scroll position for mobile indicators
  useEffect(() => {
    const slider = window.document.getElementById(sliderId);
    const isMobileView = window.innerWidth <= 425;

    if (slider && hasListings && isMobileView) {
      let timeoutId;

      const handleScroll = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const scrollLeft = slider.scrollLeft;
          const slideWidth = slider.clientWidth;
          const newSlide = Math.round(scrollLeft / slideWidth);
          setCurrentSlide(Math.max(0, Math.min(newSlide, listings.length - 1)));
        }, 100);
      };

      const handleScrollEnd = () => {
        const scrollLeft = slider.scrollLeft;
        const slideWidth = slider.clientWidth;
        const newSlide = Math.round(scrollLeft / slideWidth);
        setCurrentSlide(Math.max(0, Math.min(newSlide, listings.length - 1)));
      };

      setTimeout(() => handleScrollEnd(), 100);

      slider.addEventListener('scroll', handleScroll);
      slider.addEventListener('scrollend', handleScrollEnd);
      slider.addEventListener('touchend', handleScrollEnd);

      return () => {
        clearTimeout(timeoutId);
        slider.removeEventListener('scroll', handleScroll);
        slider.removeEventListener('scrollend', handleScrollEnd);
        slider.removeEventListener('touchend', handleScrollEnd);
      };
    }
  }, [hasListings, listings.length, sliderId, numColumns]);

  const handleDotClick = index => {
    const slider = window.document.getElementById(sliderId);
    if (slider) {
      const slideWidth = slider.clientWidth;
      slider.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth',
      });
      setCurrentSlide(index);
    }
  };

  const onSlideLeft = e => {
    const slider = window.document.getElementById(sliderId);
    if (slider) {
      const slideWidth = slider.clientWidth;
      slider.scrollLeft = slider.scrollLeft - slideWidth;
      e.target.focus();
    }
  };

  const onSlideRight = e => {
    const slider = window.document.getElementById(sliderId);
    if (slider) {
      const slideWidth = slider.clientWidth;
      slider.scrollLeft = slider.scrollLeft + slideWidth;
      e.target.focus();
    }
  };

  const onKeyDown = e => {
    if (e.keyCode === KEY_CODE_ARROW_LEFT) {
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
      e.preventDefault();
      onSlideRight(e);
    }
  };

  if (loading) {
    return (
      <div className={classes}>
        <div className={css.header}>
          <h2 className={css.title}>
            <FormattedMessage id="RecentListings.title" />
          </h2>
          <NamedLink
            name="SearchPage"
            to={{
              search: '?sort=-createdAt',
            }}
            className={css.viewAllButton}
          >
            <FormattedMessage id="MostRecentListings.viewAll" />
          </NamedLink>
        </div>
        <div className={css.loading}>
          <IconSpinner />
        </div>
      </div>
    );
  }

  if (error || !hasListings) {
    // Don't render anything if there are no recent listings or an error occurred
    return null;
  }

  const getColumnCSS = () => {
    return numColumns === 4
      ? css.fourColumns
      : numColumns === 3
      ? css.threeColumns
      : numColumns === 2
      ? css.twoColumns
      : css.oneColumn;
  };

  return (
    <section className={classes}>
      <div className={css.contentWrapper}>
        <div className={css.header}>
          <h2 className={css.title}>
            <FormattedMessage id="MostRecentListings.title" />
          </h2>
          <NamedLink
            name="SearchPage"
            to={{
              search: '?sort=createdAt',
            }}
            className={css.viewAllButton}
          >
            <FormattedMessage id="MostRecentListings.viewAll" />
          </NamedLink>
          {/* <p className={css.subtitle}>
            <FormattedMessage id="RecentListings.subtitle" />
          </p> */}
        </div>

        <div className={css.carouselContainer} id={carouselContainerId}>
          <button
            className={classNames(css.carouselArrowPrev, {
              [css.notEnoughListings]: listings.length <= numColumns,
            })}
            onClick={onSlideLeft}
            onKeyDown={onKeyDown}
          >
            ‹
          </button>

          <div className={css.carouselViewport}>
            <div className={getColumnCSS()} id={sliderId}>
              {listings.map(listing => (
                <div key={listing.id.uuid} className={css.listingCard}>
                  <HomePageListingCard
                    listing={transformListingForCard(listing)}
                    rawListing={listing}
                    badgeText="New"
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            className={classNames(css.carouselArrowNext, {
              [css.notEnoughListings]: listings.length <= numColumns,
            })}
            onClick={onSlideRight}
            onKeyDown={onKeyDown}
          >
            ›
          </button>
        </div>

        {/* Mobile carousel indicators */}
        {listings.length > 1 && (
          <>
            <div className={css.carouselIndicators}>
              {listings.map((_, index) => (
                <div
                  key={index}
                  className={classNames(css.carouselDot, {
                    [css.active]: index === currentSlide,
                  })}
                  onClick={() => handleDotClick(index)}
                />
              ))}
            </div>
            <div className={css.scrollIndicator}>Swipe to see more</div>
          </>
        )}
      </div>
    </section>
  );
};

export default RecentListings;
